import type {
    PickerItemData,
    PickerItemValue,
} from "@design-system/Picker/Picker.types";
import { findToggledValue } from "./findToggledValue";

/** Every value of a tree of rows, each parent before its children. */
export const collectItemValues = <T extends PickerItemValue>(
    items: ReadonlyArray<PickerItemData<T>>,
): T[] =>
    items.flatMap((item) => [
        item.value,
        ...collectItemValues(item.children ?? []),
    ]);

interface CategoryTreeNode {
    id: string;
    subcategories?: ReadonlyArray<{ id: string }> | null;
}

/**
 * The selection after one tick in a category tree whose parents are values:
 * ticking a category adds it and its subcategories, unticking it removes them
 * all, and a subcategory toggles on its own. `undefined` when the change
 * toggled no known row.
 */
export const applyCategoryToggle = ({
    selectedIds,
    nextIds,
    categoriesById,
}: {
    selectedIds: readonly string[];
    nextIds: readonly string[];
    categoriesById: ReadonlyMap<string, CategoryTreeNode>;
}): string[] | undefined => {
    // Both sides drop ids with no row (a deleted or scoped-out category kept
    // in a saved filter), or such an id could be taken for the ticked row.
    const isKnownId = (id: string) => categoriesById.has(id);
    const toggledId = findToggledValue(
        selectedIds.filter(isKnownId),
        nextIds.filter(isKnownId),
    );
    const toggled =
        toggledId === undefined ? undefined : categoriesById.get(toggledId);
    if (toggled === undefined) {
        return undefined;
    }
    const toggledIds = [
        toggled.id,
        ...(toggled.subcategories ?? []).map(({ id }) => id),
    ];
    return selectedIds.includes(toggled.id)
        ? selectedIds.filter((id) => !toggledIds.includes(id))
        : [...new Set([...selectedIds, ...toggledIds])];
};

/**
 * A multi-select change applied to a value the list only partly shows. The
 * Picker drops values it has no row for (filtered out by the search, scoped
 * away, not loaded), so values outside `knownValues` keep their previous
 * state while the known ones follow `next`; the previous order is kept and
 * additions are appended.
 */
export const mergeValueChange = <T extends PickerItemValue>({
    previous,
    next,
    knownValues,
}: {
    previous: readonly T[];
    next: readonly T[];
    knownValues: ReadonlySet<T>;
}): T[] => {
    const nextSet = new Set(next);
    const keptValues = previous.filter(
        (value) => !knownValues.has(value) || nextSet.has(value),
    );
    const keptSet = new Set(keptValues);
    return [...keptValues, ...next.filter((value) => !keptSet.has(value))];
};
